import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Mail, Shield, Building2,
  RefreshCw, Plus, X, Eye, EyeOff, Camera, Upload, Save,
  ChevronRight, Clock, Globe, Award, CheckCircle2, XCircle,
  MoreVertical, AlertCircle, FileText, Calendar, Phone, MapPin, Plane
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';
import OEMAccessDenied from './OEMAccessDenied';

const OEMUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);

  // Documents section
  const [userDocuments, setUserDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentStatusFilter, setDocumentStatusFilter] = useState('all');

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [createSection, setCreateSection] = useState('basic');
  const [newUserQualifications, setNewUserQualifications] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    phone_number: '',
    date_of_birth: '',
    personal_address: '',
    closest_airport: '',
    project: '',
    role: 'master',
    can_approve_nominations: false,
    can_verify_documents: true,
    can_revoke_authorization: false,
    can_create_toolbox: true,
  });

  // Edit user modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editingUserProjects, setEditingUserProjects] = useState([]);
  const [editSection, setEditSection] = useState('basic');
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [qualificationCategories, setQualificationCategories] = useState([]);
  const [editUserQualifications, setEditUserQualifications] = useState([]);
  const [editData, setEditData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    new_password: '',
    confirm_new_password: '',
    phone_number: '',
    date_of_birth: '',
    personal_address: '',
    closest_airport: '',
  });

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/oem/project-team/');
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      toast.error('Failed to load users');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/oem/projects/');
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchUserDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const response = await api.get('/userqualification/');
      setUserDocuments(response.data);
    } catch (err) {
      console.error('Error fetching user documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const getDocumentStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', label: 'No expiry', color: 'slate' };
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expired', color: 'red', days: Math.abs(daysUntilExpiry) };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: 'Expiring soon', color: 'amber', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 60) {
      return { status: 'warning', label: 'Expires in 60 days', color: 'yellow', days: daysUntilExpiry };
    }
    return { status: 'valid', label: 'Valid', color: 'emerald', days: daysUntilExpiry };
  };

  const fetchQualificationData = async () => {
    try {
      const [typesRes, categoriesRes] = await Promise.all([
        api.get('/qualificationtype/'),
        api.get('/qualificationcategory/'),
      ]);
      setQualificationTypes(typesRes.data);
      setQualificationCategories(categoriesRes.data);
    } catch (err) {
      console.error('Error fetching qualification data:', err);
    }
  };

  const groupQualificationsByCategory = (qualifications) => {
    const grouped = {};
    qualifications.forEach(qual => {
      const categoryId = qual.category || 'uncategorized';
      const category = qualificationCategories.find(c => c.id === categoryId);
      const categoryName = category ? category.name : 'Other';
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          id: categoryId,
          name: categoryName,
          qualifications: []
        };
      }
      grouped[categoryId].qualifications.push(qual);
    });
    return Object.values(grouped).sort((a, b) => {
      if (a.id === 'uncategorized') return 1;
      if (b.id === 'uncategorized') return -1;
      return a.name.localeCompare(b.name);
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setEditPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditUser = async (user) => {
    setEditSection('basic');

    // Fetch qualification types if not loaded
    if (qualificationTypes.length === 0) {
      await fetchQualificationData();
    }

    try {
      const response = await api.get(`/users/${user.id}/`);
      const fullUser = response.data;

      setEditingUser({
        id: user.id,
        ...fullUser
      });
      setEditData({
        username: fullUser.username || '',
        email: fullUser.email || user.email || '',
        first_name: fullUser.first_name || user.first_name || '',
        last_name: fullUser.last_name || user.last_name || '',
        new_password: '',
        confirm_new_password: '',
        phone_number: fullUser.phone_number || '',
        date_of_birth: fullUser.date_of_birth || '',
        personal_address: fullUser.personal_address || '',
        closest_airport: fullUser.closest_airport || '',
      });
      setEditPhotoPreview(fullUser.profile_picture || null);
      setEditPhotoFile(null);

      // Set user qualifications
      if (fullUser.qualifications && fullUser.qualifications.length > 0) {
        setEditUserQualifications(fullUser.qualifications.map(q => ({
          id: q.id,
          qualification_type: q.qualification_type,
          qualification_type_name: q.qualification_type_data?.name || '',
          certificate_number: q.certificate_number || '',
          expiry_date: q.expiry_date || '',
          certificate_file: q.certificate_file || null,
          isNew: false,
        })));
      } else {
        setEditUserQualifications([]);
      }

      if (user.projects && user.projects.length > 0) {
        setEditingUserProjects(user.projects.map(p => ({
          ...p,
          originalRole: p.role,
          originalPermissions: {
            can_approve_nominations: p.can_approve_nominations,
            can_verify_documents: p.can_verify_documents,
            can_revoke_authorization: p.can_revoke_authorization,
            can_create_toolbox: p.can_create_toolbox,
          }
        })));
      } else {
        setEditingUserProjects([]);
      }

      setShowEditModal(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setEditingUser({ id: user.id, ...user });
      setEditData({
        username: '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        new_password: '',
        confirm_new_password: '',
        phone_number: '',
        date_of_birth: '',
        personal_address: '',
        closest_airport: '',
      });
      setEditPhotoPreview(null);
      setEditPhotoFile(null);
      setEditingUserProjects(user.projects || []);
      setEditUserQualifications([]);
      setShowEditModal(true);
    }
  };

  const handleUpdateUser = async () => {
    if (!editData.first_name || !editData.last_name) {
      toast.error('Please fill in first name and last name');
      return;
    }
    if (editData.new_password && editData.new_password !== editData.confirm_new_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (editData.new_password && editData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setUpdating(true);
    try {
      const updateData = {
        first_name: editData.first_name,
        last_name: editData.last_name,
      };

      if (editData.username) {
        updateData.username = editData.username;
      }
      if (editData.new_password) {
        updateData.password = editData.new_password;
      }
      // Add personal fields
      updateData.phone_number = editData.phone_number || '';
      updateData.date_of_birth = editData.date_of_birth || null;
      updateData.personal_address = editData.personal_address || '';
      updateData.closest_airport = editData.closest_airport || '';

      if (editPhotoFile) {
        const formData = new FormData();
        Object.keys(updateData).forEach(key => {
          if (updateData[key] !== null && updateData[key] !== undefined) {
            formData.append(key, updateData[key]);
          }
        });
        formData.append('profile_picture', editPhotoFile);
        await api.patch(`/users/${editingUser.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.patch(`/users/${editingUser.id}/`, updateData);
      }

      // Save qualifications
      for (const qual of editUserQualifications) {
        if (qual.isNew && qual.certificate_number && qual.expiry_date) {
          // Create new qualification
          const qualData = new FormData();
          qualData.append('user', editingUser.id);
          qualData.append('qualification_type', qual.qualification_type);
          qualData.append('certificate_number', qual.certificate_number);
          qualData.append('expiry_date', qual.expiry_date);
          if (qual.newFile) {
            qualData.append('certificate_file', qual.newFile);
          }
          await api.post('/userqualification/', qualData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else if (!qual.isNew && qual.hasChanges) {
          // Update existing qualification
          const qualData = new FormData();
          qualData.append('certificate_number', qual.certificate_number);
          qualData.append('expiry_date', qual.expiry_date);
          if (qual.newFile) {
            qualData.append('certificate_file', qual.newFile);
          }
          await api.patch(`/userqualification/${qual.id}/`, qualData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      for (const proj of editingUserProjects) {
        if (proj.team_membership_id) {
          const hasChanges = proj.role !== proj.originalRole ||
            proj.can_approve_nominations !== proj.originalPermissions?.can_approve_nominations ||
            proj.can_verify_documents !== proj.originalPermissions?.can_verify_documents ||
            proj.can_revoke_authorization !== proj.originalPermissions?.can_revoke_authorization ||
            proj.can_create_toolbox !== proj.originalPermissions?.can_create_toolbox;

          if (hasChanges) {
            await api.patch(`/oem/projects/${proj.project_id}/team/${proj.team_membership_id}/`, {
              role: proj.role,
              can_approve_nominations: proj.can_approve_nominations,
              can_verify_documents: proj.can_verify_documents,
              can_revoke_authorization: proj.can_revoke_authorization,
              can_create_toolbox: proj.can_create_toolbox,
            });
          }
        }
      }

      toast.success('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      setEditPhotoFile(null);
      setEditPhotoPreview(null);
      loadData();
    } catch (err) {
      console.error('Error updating user:', err);
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const errors = Object.entries(errorData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
          toast.error(errors || 'Validation error');
        } else {
          toast.error(String(errorData.detail || errorData) || 'Failed to update user');
        }
      } else {
        toast.error('Failed to update user');
      }
    } finally {
      setUpdating(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditPhotoFile(null);
    setEditPhotoPreview(null);
    setEditingUserProjects([]);
    setEditUserQualifications([]);
    setEditSection('basic');
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchTeamMembers(), fetchProjects(), fetchUserDocuments()]);
    setLoading(false);
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await api.get('/oem/dashboard/team/');
        const roles = response.data.my_roles || [];
        const hasMasterRole = roles.some(r => r.role === 'master');
        const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;
        if (hasOnlyRestrictedRoles) {
          setAccessDenied(true);
        }
      } catch (err) {
        // If endpoint fails, user has no team roles - allow access
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (!accessDenied) {
      loadData();
    }
  }, [accessDenied]);

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.first_name || !newUser.last_name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newUser.password !== newUser.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setCreating(true);
    try {
      let userResponse;
      const userData = {
        username: newUser.username,
        email: newUser.email,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        password: newUser.password,
        is_active: true,
        is_oem_master: true,
        ...(newUser.phone_number && { phone_number: newUser.phone_number }),
        ...(newUser.date_of_birth && { date_of_birth: newUser.date_of_birth }),
        ...(newUser.personal_address && { personal_address: newUser.personal_address }),
        ...(newUser.closest_airport && { closest_airport: newUser.closest_airport }),
      };

      if (photoFile) {
        const formData = new FormData();
        Object.entries(userData).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('profile_picture', photoFile);
        userResponse = await api.post('/users/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        userResponse = await api.post('/users/', userData);
      }

      const createdUser = userResponse.data;

      if (newUser.project) {
        await api.post(`/oem/projects/${newUser.project}/team/`, {
          user: createdUser.id,
          role: newUser.role,
          can_approve_nominations: newUser.can_approve_nominations,
          can_verify_documents: newUser.can_verify_documents,
          can_revoke_authorization: newUser.can_revoke_authorization,
          can_create_toolbox: newUser.can_create_toolbox,
        });
      }

      // Save qualifications for new user
      for (const qual of newUserQualifications) {
        if (qual.certificate_number && qual.expiry_date) {
          const qualData = new FormData();
          qualData.append('user', createdUser.id);
          qualData.append('qualification_type', qual.qualification_type);
          qualData.append('certificate_number', qual.certificate_number);
          qualData.append('expiry_date', qual.expiry_date);
          if (qual.certificate_file) {
            qualData.append('certificate_file', qual.certificate_file);
          }
          await api.post('/userqualification/', qualData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      toast.success(`User ${newUser.first_name} ${newUser.last_name} created successfully!`);
      setShowCreateModal(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setCreateSection('basic');
      setNewUserQualifications([]);
      setNewUser({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirm_password: '',
        phone_number: '',
        date_of_birth: '',
        personal_address: '',
        closest_airport: '',
        project: '',
        role: 'master',
        can_approve_nominations: false,
        can_verify_documents: true,
        can_revoke_authorization: false,
        can_create_toolbox: true,
      });
      fetchTeamMembers();
    } catch (err) {
      console.error('Error creating user:', err);
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const errors = Object.entries(errorData)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
          toast.error(errors || 'Validation error');
        } else {
          toast.error(String(errorData.detail || errorData) || 'Failed to create user');
        }
      } else {
        toast.error('Failed to create user');
      }
    } finally {
      setCreating(false);
    }
  };

  const getRoleStyle = (role) => {
    const styles = {
      site_manager: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', label: 'Site Manager' },
      hse_manager: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', label: 'HSE Manager' },
      master: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', label: 'Master' },
    };
    return styles[role] || { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', label: role };
  };

  // Group by user to avoid duplicates
  const usersMap = new Map();
  teamMembers.forEach(member => {
    const existing = usersMap.get(member.user);
    if (existing) {
      existing.projects.push({
        id: member.project,
        project_id: member.project,
        name: member.project_name,
        code: member.project_code,
        role: member.role,
        role_display: member.role_display,
        team_membership_id: member.id,
        can_approve_nominations: member.can_approve_nominations,
        can_verify_documents: member.can_verify_documents,
        can_revoke_authorization: member.can_revoke_authorization,
        can_create_toolbox: member.can_create_toolbox,
      });
    } else {
      usersMap.set(member.user, {
        id: member.user,
        first_name: member.user_first_name,
        last_name: member.user_last_name,
        email: member.user_email,
        profile_picture: member.user_profile_picture,
        projects: [{
          id: member.project,
          project_id: member.project,
          name: member.project_name,
          code: member.project_code,
          role: member.role,
          role_display: member.role_display,
          team_membership_id: member.id,
          can_approve_nominations: member.can_approve_nominations,
          can_verify_documents: member.can_verify_documents,
          can_revoke_authorization: member.can_revoke_authorization,
          can_create_toolbox: member.can_create_toolbox,
        }]
      });
    }
  });

  const users = Array.from(usersMap.values());

  const filteredUsers = users.filter(user => {
    const matchesSearch = !search ||
      user.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase());

    const matchesRole = roleFilter === 'all' ||
      user.projects.some(p => p.role === roleFilter);

    return matchesSearch && matchesRole;
  });

  // Stats
  const stats = {
    total: users.length,
    masters: users.filter(u => u.projects.some(p => p.role === 'master')).length,
    hse: users.filter(u => u.projects.some(p => p.role === 'hse_manager')).length,
    site: users.filter(u => u.projects.some(p => p.role === 'site_manager')).length,
  };

  if (accessDenied) {
    return <OEMAccessDenied message="You don't have permission to manage Users." />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">Loading personnel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="text-slate-900">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Action Buttons */}
        <div className="flex justify-end items-center gap-3 mb-6">
          <button
            onClick={loadData}
            className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-all shadow-sm"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="h-10 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-black text-[11px] uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-orange-500/20"
          >
            <Plus className="h-4 w-4" />
            Add Personnel
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <MetricCard title="Total Personnel" value={stats.total} icon={<Users size={18} />} />
          <MetricCard title="Masters" value={stats.masters} icon={<Shield size={18} />} color="orange" />
          <MetricCard title="HSE Managers" value={stats.hse} icon={<Shield size={18} />} color="emerald" />
          <MetricCard title="Site Managers" value={stats.site} icon={<Shield size={18} />} color="blue" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm transition-all"
              />
            </div>
            <div className="flex bg-slate-100 p-1 rounded-xl">
              {[
                { key: 'all', label: 'All' },
                { key: 'master', label: 'Master' },
                { key: 'hse_manager', label: 'HSE' },
                { key: 'site_manager', label: 'Site' },
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setRoleFilter(filter.key)}
                  className={`px-3 py-1.5 text-[10px] font-black rounded-lg uppercase transition-all ${
                    roleFilter === filter.key
                      ? 'bg-orange-500 text-white shadow-lg'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left - Personnel List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-[10px] font-black tracking-[0.2em] uppercase text-orange-500 px-1">
              Operational Personnel
            </h3>
            <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
              {filteredUsers.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Users size={20} className="text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-xs font-medium">No personnel found</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`w-full p-4 rounded-2xl border transition-all text-left flex items-center gap-4 ${
                      selectedUser?.id === user.id
                        ? 'bg-orange-500 border-orange-400 text-white scale-[1.01] shadow-lg shadow-orange-500/20'
                        : 'bg-white border-slate-200 hover:border-orange-300 text-slate-900 shadow-sm'
                    }`}
                  >
                    {user.profile_picture ? (
                      <img
                        src={user.profile_picture}
                        alt=""
                        className={`w-10 h-10 rounded-xl object-cover border-2 ${selectedUser?.id === user.id ? 'border-white/30' : 'border-slate-200'}`}
                      />
                    ) : (
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${
                        selectedUser?.id === user.id
                          ? 'bg-white/20 text-white'
                          : 'bg-orange-100 text-orange-600'
                      }`}>
                        {user.first_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="block font-black text-sm leading-tight truncate">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className={`block text-[9px] font-bold uppercase tracking-widest mt-0.5 ${
                        selectedUser?.id === user.id ? 'text-white/70' : 'text-orange-500'
                      }`}>
                        {user.projects[0]?.role_display || 'No Role'}
                      </span>
                    </div>
                    <ChevronRight size={14} className={selectedUser?.id === user.id ? 'text-white/70' : 'text-slate-400'} />
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right - User Details */}
          <div className="lg:col-span-2">
            {selectedUser ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-8 relative overflow-hidden shadow-sm">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 blur-[100px] rounded-full"></div>

                <div className="flex flex-col md:flex-row gap-8 items-start relative z-10">
                  {selectedUser.profile_picture ? (
                    <img
                      src={selectedUser.profile_picture}
                      alt=""
                      className="w-28 h-28 rounded-2xl border-2 border-orange-500 shadow-xl object-cover"
                    />
                  ) : (
                    <div className="w-28 h-28 rounded-2xl border-2 border-orange-500 shadow-xl bg-orange-100 flex items-center justify-center">
                      <span className="text-4xl font-black text-orange-600">
                        {selectedUser.first_name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}

                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                          {selectedUser.first_name} {selectedUser.last_name}
                        </h2>
                        <p className="text-orange-500 font-black text-[10px] tracking-[0.2em] uppercase mt-1">
                          {selectedUser.projects[0]?.role_display || 'Team Member'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleEditUser(selectedUser)}
                        className="p-2 bg-slate-100 rounded-xl border border-slate-200 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                        <span className="block text-[8px] text-slate-500 uppercase font-black tracking-widest mb-1">Email Address</span>
                        <span className="flex items-center gap-2 text-xs font-black text-slate-900 leading-none">
                          <Mail size={12} className="text-orange-500" />
                          {selectedUser.email}
                        </span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                        <span className="block text-[8px] text-slate-500 uppercase font-black tracking-widest mb-1">Assigned Projects</span>
                        <span className="flex items-center gap-2 text-xs font-black text-slate-900 leading-none">
                          <Building2 size={12} className="text-orange-500" />
                          {selectedUser.projects.length} Project{selectedUser.projects.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Assignments */}
                <div className="mt-8 relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xs font-black flex items-center gap-3 text-slate-900 uppercase tracking-tight">
                      <Building2 size={14} className="text-orange-500" />
                      Project Assignments & Permissions
                    </h4>
                    <div className="h-px flex-1 bg-slate-200 mx-4"></div>
                  </div>

                  <div className="space-y-3">
                    {selectedUser.projects.map((project, idx) => {
                      const roleStyle = getRoleStyle(project.role);
                      return (
                        <div
                          key={idx}
                          className="p-4 bg-slate-50 rounded-2xl border border-slate-200 group hover:border-orange-300 transition-all"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <span className="text-[9px] font-black text-orange-500 tracking-widest">{project.code}</span>
                              <h5 className="font-black text-sm text-slate-900">{project.name}</h5>
                            </div>
                            <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase border ${roleStyle.bg} ${roleStyle.text} ${roleStyle.border}`}>
                              {roleStyle.label}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <PermissionBadge
                              label="Approve"
                              active={project.can_approve_nominations}
                            />
                            <PermissionBadge
                              label="Verify"
                              active={project.can_verify_documents}
                            />
                            <PermissionBadge
                              label="Revoke"
                              active={project.can_revoke_authorization}
                            />
                            <PermissionBadge
                              label="Toolbox"
                              active={project.can_create_toolbox}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center bg-white border border-dashed border-slate-300 rounded-3xl text-slate-500 p-16 text-center group min-h-[400px]">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-6 border border-slate-200 group-hover:border-orange-300 transition-all">
                  <Users size={24} className="text-orange-500/40" />
                </div>
                <h3 className="text-sm font-black text-slate-500 uppercase tracking-tighter">No Personnel Selected</h3>
                <p className="text-[10px] max-w-xs mt-2 font-medium text-slate-400 leading-relaxed">
                  Select a team member from the list to view their profile and manage authorizations.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Personnel Documents Section */}
        <div className="mt-8">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                  <FileText size={20} className="text-orange-500" />
                  Personnel Documents
                </h3>
                <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase mt-1">
                  Certificate and qualification tracking
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={documentSearch}
                    onChange={(e) => setDocumentSearch(e.target.value)}
                    placeholder="Search documents..."
                    className="h-9 pl-9 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-xs w-64"
                  />
                </div>
                <div className="flex bg-slate-100 p-1 rounded-xl">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'valid', label: 'Valid' },
                    { key: 'expiring', label: 'Expiring' },
                    { key: 'expired', label: 'Expired' },
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setDocumentStatusFilter(filter.key)}
                      className={`px-2.5 py-1 text-[9px] font-black rounded-lg uppercase transition-all ${
                        documentStatusFilter === filter.key
                          ? 'bg-orange-500 text-white shadow-lg'
                          : 'text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {documentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Employee</th>
                      <th className="text-left py-3 px-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Certificate</th>
                      <th className="text-left py-3 px-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Certificate No.</th>
                      <th className="text-left py-3 px-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Expiry Date</th>
                      <th className="text-left py-3 px-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userDocuments
                      .filter(doc => {
                        const status = getDocumentStatus(doc.expiry_date);
                        const matchesStatus = documentStatusFilter === 'all' ||
                          (documentStatusFilter === 'valid' && status.status === 'valid') ||
                          (documentStatusFilter === 'expiring' && (status.status === 'expiring' || status.status === 'warning')) ||
                          (documentStatusFilter === 'expired' && status.status === 'expired');
                        const matchesSearch = !documentSearch ||
                          doc.user_full_name?.toLowerCase().includes(documentSearch.toLowerCase()) ||
                          doc.qualification_type_data?.name?.toLowerCase().includes(documentSearch.toLowerCase()) ||
                          doc.certificate_number?.toLowerCase().includes(documentSearch.toLowerCase());
                        return matchesStatus && matchesSearch;
                      })
                      .slice(0, 20)
                      .map((doc, idx) => {
                        const status = getDocumentStatus(doc.expiry_date);
                        const statusColors = {
                          valid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                          expiring: 'bg-amber-100 text-amber-700 border-amber-200',
                          warning: 'bg-yellow-100 text-yellow-700 border-yellow-200',
                          expired: 'bg-red-100 text-red-700 border-red-200',
                          unknown: 'bg-slate-100 text-slate-600 border-slate-200',
                        };
                        return (
                          <tr key={doc.id || idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4">
                              <span className="font-bold text-sm text-slate-900">{doc.user_full_name || 'Unknown'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-sm text-slate-700">{doc.qualification_type_data?.name || 'Unknown'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs text-slate-500 font-mono">{doc.certificate_number || '-'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-xs text-slate-600">
                                {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : '-'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase border ${statusColors[status.status]}`}>
                                {status.label}
                                {status.days !== undefined && status.status !== 'expired' && ` (${status.days}d)`}
                                {status.status === 'expired' && ` (${status.days}d ago)`}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {userDocuments.length === 0 && (
                  <div className="text-center py-12">
                    <FileText size={32} className="text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">No documents found</p>
                  </div>
                )}
                {userDocuments.length > 20 && (
                  <div className="text-center py-4 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Showing first 20 of {userDocuments.length} documents</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => { setShowCreateModal(false); setPhotoFile(null); setPhotoPreview(null); setCreateSection('basic'); setNewUserQualifications([]); }}>
            <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Plus size={20} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Add New Personnel</h3>
                    <p className="text-[10px] text-orange-500 font-bold tracking-widest uppercase">Create user and assign to project</p>
                  </div>
                </div>
                <button onClick={() => { setShowCreateModal(false); setPhotoFile(null); setPhotoPreview(null); setCreateSection('basic'); setNewUserQualifications([]); }} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-slate-200 flex-shrink-0">
                <div className="flex gap-1">
                  {[
                    { key: 'basic', label: 'Basic Info', icon: Users },
                    { key: 'personal', label: 'Personal', icon: MapPin },
                    { key: 'certificates', label: 'Certificates', icon: Award },
                    { key: 'project', label: 'Project & Permissions', icon: Shield },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setCreateSection(tab.key);
                        if (tab.key === 'certificates' && qualificationTypes.length === 0) {
                          fetchQualificationData();
                        }
                      }}
                      className={`px-4 py-3 text-[10px] font-black uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                        createSection === tab.key
                          ? 'border-orange-500 text-orange-600 bg-orange-50/50'
                          : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <tab.icon size={14} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Basic Info Tab */}
                {createSection === 'basic' && (
                  <div className="flex gap-6">
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">First Name *</label>
                          <input
                            type="text"
                            value={newUser.first_name}
                            onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Last Name *</label>
                          <input
                            type="text"
                            value={newUser.last_name}
                            onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Username *</label>
                          <input
                            type="text"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Email *</label>
                          <input
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Password *</label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={newUser.password}
                              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                              className="w-full h-10 px-3 pr-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-orange-500"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Confirm Password *</label>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newUser.confirm_password}
                            onChange={(e) => setNewUser({ ...newUser, confirm_password: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-48 flex-shrink-0">
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Profile Photo</label>
                      <div className="relative">
                        <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" id="photo-upload" />
                        <label htmlFor="photo-upload" className="block cursor-pointer">
                          {photoPreview ? (
                            <div className="relative group">
                              <img src={photoPreview} alt="Preview" className="w-48 h-48 object-cover rounded-2xl border-2 border-orange-500" />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center">
                                <Camera className="h-8 w-8 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-48 h-48 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 hover:border-orange-400 transition-all">
                              <Upload className="h-8 w-8 text-slate-400 mb-2" />
                              <span className="text-xs text-slate-500 font-medium">Upload photo</span>
                            </div>
                          )}
                        </label>
                        {photoPreview && (
                          <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Personal Tab */}
                {createSection === 'personal' && (
                  <div className="space-y-4 max-w-2xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          <Phone size={10} className="inline mr-1" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newUser.phone_number}
                          onChange={(e) => setNewUser({ ...newUser, phone_number: e.target.value })}
                          placeholder="+48 123 456 789"
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          <Calendar size={10} className="inline mr-1" />
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={newUser.date_of_birth}
                          onChange={(e) => setNewUser({ ...newUser, date_of_birth: e.target.value })}
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        <MapPin size={10} className="inline mr-1" />
                        Personal Address
                      </label>
                      <input
                        type="text"
                        value={newUser.personal_address}
                        onChange={(e) => setNewUser({ ...newUser, personal_address: e.target.value })}
                        placeholder="Street, City, Country"
                        className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        <Plane size={10} className="inline mr-1" />
                        Closest Airport
                      </label>
                      <input
                        type="text"
                        value={newUser.closest_airport}
                        onChange={(e) => setNewUser({ ...newUser, closest_airport: e.target.value })}
                        placeholder="e.g., WAW, FRA, AMS"
                        className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Certificates Tab */}
                {createSection === 'certificates' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-slate-600">
                        Fill in certificate details for this user.
                        <span className="ml-2 text-orange-500 font-bold">
                          ({newUserQualifications.filter(q => q.certificate_number && q.expiry_date).length}/{qualificationTypes.length} filled)
                        </span>
                      </p>
                    </div>

                    {qualificationTypes.length === 0 ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {groupQualificationsByCategory(qualificationTypes).map(category => (
                          <div key={category.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">
                                {category.name}
                              </span>
                              <span className="text-[9px] text-slate-400">
                                {category.qualifications.filter(q => {
                                  const userQual = newUserQualifications.find(eq => eq.qualification_type === q.id);
                                  return userQual?.certificate_number && userQual?.expiry_date;
                                }).length}/{category.qualifications.length} filled
                              </span>
                            </div>
                            <div className="divide-y divide-slate-100">
                              {category.qualifications.map((qualType) => {
                                const userQual = newUserQualifications.find(eq => eq.qualification_type === qualType.id);
                                const isFilled = userQual?.certificate_number && userQual?.expiry_date;

                                return (
                                  <div key={qualType.id} className={`p-4 transition-colors ${isFilled ? 'bg-emerald-50/30' : 'hover:bg-slate-50'}`}>
                                    <div className="grid grid-cols-4 gap-3 items-center">
                                      <div>
                                        <span className="text-sm font-bold text-slate-900">{qualType.name}</span>
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Certificate Number</label>
                                        <input
                                          type="text"
                                          value={userQual?.certificate_number || ''}
                                          onChange={(e) => {
                                            const newQuals = [...newUserQualifications];
                                            const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                            if (idx >= 0) {
                                              newQuals[idx] = { ...newQuals[idx], certificate_number: e.target.value };
                                            } else {
                                              newQuals.push({
                                                qualification_type: qualType.id,
                                                qualification_type_name: qualType.name,
                                                certificate_number: e.target.value,
                                                expiry_date: '',
                                                certificate_file: null,
                                              });
                                            }
                                            setNewUserQualifications(newQuals);
                                          }}
                                          placeholder="e.g., CERT-12345"
                                          className="w-full h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Expiry Date</label>
                                        <input
                                          type="date"
                                          value={userQual?.expiry_date || ''}
                                          onChange={(e) => {
                                            const newQuals = [...newUserQualifications];
                                            const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                            if (idx >= 0) {
                                              newQuals[idx] = { ...newQuals[idx], expiry_date: e.target.value };
                                            } else {
                                              newQuals.push({
                                                qualification_type: qualType.id,
                                                qualification_type_name: qualType.name,
                                                certificate_number: '',
                                                expiry_date: e.target.value,
                                                certificate_file: null,
                                              });
                                            }
                                            setNewUserQualifications(newQuals);
                                          }}
                                          className="w-full h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Certificate File</label>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) {
                                                const newQuals = [...newUserQualifications];
                                                const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                                if (idx >= 0) {
                                                  newQuals[idx] = { ...newQuals[idx], certificate_file: file };
                                                } else {
                                                  newQuals.push({
                                                    qualification_type: qualType.id,
                                                    qualification_type_name: qualType.name,
                                                    certificate_number: '',
                                                    expiry_date: '',
                                                    certificate_file: file,
                                                  });
                                                }
                                                setNewUserQualifications(newQuals);
                                              }
                                            }}
                                            className="hidden"
                                            id={`create-cert-file-${qualType.id}`}
                                          />
                                          <label
                                            htmlFor={`create-cert-file-${qualType.id}`}
                                            className={`flex-1 h-9 px-2 rounded-lg border text-xs flex items-center gap-1 cursor-pointer transition-colors ${
                                              userQual?.certificate_file
                                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                                : 'border-slate-200 bg-white text-slate-400 hover:border-orange-300 hover:bg-orange-50'
                                            }`}
                                          >
                                            <FileText size={12} />
                                            <span className="truncate">
                                              {userQual?.certificate_file?.name || 'Upload PDF'}
                                            </span>
                                          </label>
                                          {userQual?.certificate_file && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newQuals = [...newUserQualifications];
                                                const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                                if (idx >= 0) {
                                                  newQuals[idx] = { ...newQuals[idx], certificate_file: null };
                                                  setNewUserQualifications(newQuals);
                                                }
                                              }}
                                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                                            >
                                              <X size={12} />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Project & Permissions Tab */}
                {createSection === 'project' && (
                  <div className="space-y-4 max-w-2xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Assign to Project</label>
                        <select
                          value={newUser.project}
                          onChange={(e) => setNewUser({ ...newUser, project: e.target.value })}
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        >
                          <option value="">Select a project...</option>
                          {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.code} - {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Role in Project</label>
                        <select
                          value={newUser.role}
                          onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        >
                          <option value="master">Master</option>
                          <option value="hse_manager">HSE Manager</option>
                          <option value="site_manager">Site Manager</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest">Permissions</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'can_approve_nominations', label: 'Can approve nominations' },
                          { key: 'can_verify_documents', label: 'Can verify documents' },
                          { key: 'can_revoke_authorization', label: 'Can revoke authorization' },
                          { key: 'can_create_toolbox', label: 'Can create toolbox talks' },
                        ].map((perm) => (
                          <label key={perm.key} className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer border border-slate-200 transition-all">
                            <input
                              type="checkbox"
                              checked={newUser[perm.key]}
                              onChange={(e) => setNewUser({ ...newUser, [perm.key]: e.target.checked })}
                              className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                            />
                            <span className="text-xs text-slate-700 font-medium">{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 flex-shrink-0">
                <button
                  onClick={() => { setShowCreateModal(false); setPhotoFile(null); setPhotoPreview(null); setCreateSection('basic'); setNewUserQualifications([]); }}
                  className="h-9 px-4 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 font-black text-[10px] uppercase tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={creating}
                  className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 font-black text-[10px] uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={closeEditModal}>
            <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-4">
                  {editPhotoPreview ? (
                    <img src={editPhotoPreview} alt="" className="w-12 h-12 rounded-xl object-cover border-2 border-orange-500" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <span className="text-lg font-black text-orange-600">{editData.first_name?.charAt(0) || 'U'}</span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                      {editData.first_name} {editData.last_name}
                    </h3>
                    <p className="text-[10px] text-orange-500 font-bold tracking-widest uppercase">Edit Personnel Profile</p>
                  </div>
                </div>
                <button onClick={closeEditModal} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-slate-200 flex-shrink-0">
                <div className="flex gap-1">
                  {[
                    { key: 'basic', label: 'Basic Info', icon: Users },
                    { key: 'personal', label: 'Personal', icon: MapPin },
                    { key: 'certificates', label: 'Certificates', icon: Award },
                    { key: 'permissions', label: 'Permissions', icon: Shield },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setEditSection(tab.key)}
                      className={`px-4 py-3 text-[10px] font-black uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                        editSection === tab.key
                          ? 'border-orange-500 text-orange-600 bg-orange-50/50'
                          : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <tab.icon size={14} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Basic Info Tab */}
                {editSection === 'basic' && (
                  <div className="flex gap-6">
                    <div className="flex-1 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">First Name *</label>
                          <input
                            type="text"
                            value={editData.first_name}
                            onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Last Name *</label>
                          <input
                            type="text"
                            value={editData.last_name}
                            onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Username</label>
                          <input
                            type="text"
                            value={editData.username}
                            onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Email</label>
                          <input
                            type="email"
                            value={editData.email}
                            disabled
                            className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed text-sm"
                          />
                        </div>
                      </div>
                      <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50">
                        <h4 className="text-[10px] font-black text-slate-500 mb-3 uppercase tracking-widest">Change Password (optional)</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">New Password</label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={editData.new_password}
                                onChange={(e) => setEditData({ ...editData, new_password: e.target.value })}
                                placeholder="Leave blank to keep current"
                                className="w-full h-10 px-3 pr-10 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-orange-500"
                              >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Confirm New Password</label>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={editData.confirm_new_password}
                              onChange={(e) => setEditData({ ...editData, confirm_new_password: e.target.value })}
                              placeholder="Confirm new password"
                              className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="w-48 flex-shrink-0">
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Profile Photo</label>
                      <div className="relative">
                        <input type="file" accept="image/*" onChange={handleEditPhotoChange} className="hidden" id="edit-photo-upload" />
                        <label htmlFor="edit-photo-upload" className="block cursor-pointer">
                          {editPhotoPreview ? (
                            <div className="relative group">
                              <img src={editPhotoPreview} alt="Preview" className="w-48 h-48 object-cover rounded-2xl border-2 border-orange-500" />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-center justify-center">
                                <Camera className="h-8 w-8 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-48 h-48 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 hover:border-orange-400 transition-all">
                              <Upload className="h-8 w-8 text-slate-400 mb-2" />
                              <span className="text-xs text-slate-500 font-medium">Upload photo</span>
                            </div>
                          )}
                        </label>
                        {editPhotoPreview && (
                          <button type="button" onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null); }} className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Personal Tab */}
                {editSection === 'personal' && (
                  <div className="space-y-4 max-w-2xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          <Phone size={10} className="inline mr-1" />
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={editData.phone_number}
                          onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                          placeholder="+48 123 456 789"
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                          <Calendar size={10} className="inline mr-1" />
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={editData.date_of_birth}
                          onChange={(e) => setEditData({ ...editData, date_of_birth: e.target.value })}
                          className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        <MapPin size={10} className="inline mr-1" />
                        Personal Address
                      </label>
                      <input
                        type="text"
                        value={editData.personal_address}
                        onChange={(e) => setEditData({ ...editData, personal_address: e.target.value })}
                        placeholder="Street, City, Country"
                        className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5">
                        <Plane size={10} className="inline mr-1" />
                        Closest Airport
                      </label>
                      <input
                        type="text"
                        value={editData.closest_airport}
                        onChange={(e) => setEditData({ ...editData, closest_airport: e.target.value })}
                        placeholder="e.g., WAW, FRA, AMS"
                        className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Certificates Tab */}
                {editSection === 'certificates' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-slate-600">
                        Fill in certificate details for this user.
                        <span className="ml-2 text-orange-500 font-bold">
                          ({editUserQualifications.filter(q => q.certificate_number && q.expiry_date).length}/{qualificationTypes.length} filled)
                        </span>
                      </p>
                    </div>

                    <div className="space-y-4">
                      {groupQualificationsByCategory(qualificationTypes).map(category => (
                        <div key={category.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                          <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                            <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">
                              {category.name}
                            </span>
                            <span className="text-[9px] text-slate-400">
                              {category.qualifications.filter(q => {
                                const userQual = editUserQualifications.find(eq => eq.qualification_type === q.id);
                                return userQual?.certificate_number && userQual?.expiry_date;
                              }).length}/{category.qualifications.length} filled
                            </span>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {category.qualifications.map((qualType) => {
                              const userQual = editUserQualifications.find(eq => eq.qualification_type === qualType.id);
                              const status = userQual?.expiry_date ? getDocumentStatus(userQual.expiry_date) : null;
                              const isFilled = userQual?.certificate_number && userQual?.expiry_date;
                              const hasFile = userQual?.certificate_file || userQual?.newFile;

                              return (
                                <div key={qualType.id} className={`p-4 transition-colors ${isFilled ? 'bg-emerald-50/30' : 'hover:bg-slate-50'}`}>
                                  <div className="grid grid-cols-5 gap-3 items-center">
                                    <div>
                                      <span className="text-sm font-bold text-slate-900">{qualType.name}</span>
                                    </div>
                                    <div>
                                      <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Certificate Number</label>
                                      <input
                                        type="text"
                                        value={userQual?.certificate_number || ''}
                                        onChange={(e) => {
                                          const newQuals = [...editUserQualifications];
                                          const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                          if (idx >= 0) {
                                            newQuals[idx] = { ...newQuals[idx], certificate_number: e.target.value, hasChanges: true };
                                          } else {
                                            newQuals.push({
                                              id: `new-${qualType.id}`,
                                              qualification_type: qualType.id,
                                              qualification_type_name: qualType.name,
                                              certificate_number: e.target.value,
                                              expiry_date: '',
                                              isNew: true,
                                            });
                                          }
                                          setEditUserQualifications(newQuals);
                                        }}
                                        placeholder="e.g., CERT-12345"
                                        className="w-full h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Expiry Date</label>
                                      <input
                                        type="date"
                                        value={userQual?.expiry_date || ''}
                                        onChange={(e) => {
                                          const newQuals = [...editUserQualifications];
                                          const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                          if (idx >= 0) {
                                            newQuals[idx] = { ...newQuals[idx], expiry_date: e.target.value, hasChanges: true };
                                          } else {
                                            newQuals.push({
                                              id: `new-${qualType.id}`,
                                              qualification_type: qualType.id,
                                              qualification_type_name: qualType.name,
                                              certificate_number: '',
                                              expiry_date: e.target.value,
                                              isNew: true,
                                            });
                                          }
                                          setEditUserQualifications(newQuals);
                                        }}
                                        className="w-full h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Certificate File</label>
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              const newQuals = [...editUserQualifications];
                                              const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                              if (idx >= 0) {
                                                newQuals[idx] = { ...newQuals[idx], newFile: file, hasChanges: true };
                                              } else {
                                                newQuals.push({
                                                  id: `new-${qualType.id}`,
                                                  qualification_type: qualType.id,
                                                  qualification_type_name: qualType.name,
                                                  certificate_number: '',
                                                  expiry_date: '',
                                                  newFile: file,
                                                  isNew: true,
                                                });
                                              }
                                              setEditUserQualifications(newQuals);
                                            }
                                          }}
                                          className="hidden"
                                          id={`edit-cert-file-${qualType.id}`}
                                        />
                                        <label
                                          htmlFor={`edit-cert-file-${qualType.id}`}
                                          className={`flex-1 h-9 px-2 rounded-lg border text-xs flex items-center gap-1 cursor-pointer transition-colors ${
                                            hasFile
                                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                              : 'border-slate-200 bg-white text-slate-400 hover:border-orange-300 hover:bg-orange-50'
                                          }`}
                                        >
                                          <FileText size={12} />
                                          <span className="truncate text-[10px]">
                                            {userQual?.newFile?.name || (userQual?.certificate_file ? 'View file' : 'Upload')}
                                          </span>
                                        </label>
                                        {hasFile && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newQuals = [...editUserQualifications];
                                              const idx = newQuals.findIndex(q => q.qualification_type === qualType.id);
                                              if (idx >= 0) {
                                                newQuals[idx] = { ...newQuals[idx], newFile: null, hasChanges: true };
                                                setEditUserQualifications(newQuals);
                                              }
                                            }}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                                          >
                                            <X size={12} />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-[8px] font-black text-slate-400 uppercase mb-1">Status</label>
                                      {status ? (
                                        <span className={`inline-block px-2 py-1 rounded-lg text-[9px] font-black uppercase ${
                                          status.status === 'valid' ? 'bg-emerald-100 text-emerald-700' :
                                          status.status === 'expiring' || status.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                                          status.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                          {status.label}
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-slate-300">—</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Permissions Tab */}
                {editSection === 'permissions' && (
                  <div className="space-y-4">
                    {editingUserProjects.length > 0 ? (
                      <div className="space-y-4">
                        {editingUserProjects.map((proj, idx) => (
                          <div key={proj.project_id || idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <span className="text-[9px] font-black text-orange-500 tracking-widest">{proj.project_code}</span>
                                <p className="font-black text-sm text-slate-900">{proj.project_name}</p>
                              </div>
                              <select
                                value={proj.role}
                                onChange={(e) => {
                                  const newProjects = [...editingUserProjects];
                                  newProjects[idx] = { ...newProjects[idx], role: e.target.value };
                                  setEditingUserProjects(newProjects);
                                }}
                                className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-xs font-black"
                              >
                                <option value="master">Master</option>
                                <option value="hse_manager">HSE Manager</option>
                                <option value="site_manager">Site Manager</option>
                              </select>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {[
                                { key: 'can_approve_nominations', label: 'Approve nominations' },
                                { key: 'can_verify_documents', label: 'Verify documents' },
                                { key: 'can_revoke_authorization', label: 'Revoke authorization' },
                                { key: 'can_create_toolbox', label: 'Create toolbox' },
                              ].map((perm) => (
                                <label key={perm.key} className="flex items-center gap-2 p-2 bg-white rounded-xl border border-slate-200 cursor-pointer hover:border-orange-300 transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={proj[perm.key] || false}
                                    onChange={(e) => {
                                      const newProjects = [...editingUserProjects];
                                      newProjects[idx] = { ...newProjects[idx], [perm.key]: e.target.checked };
                                      setEditingUserProjects(newProjects);
                                    }}
                                    className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                  />
                                  <span className="text-xs text-slate-700 font-medium">{perm.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                        <Shield size={40} className="text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm font-medium">No project assignments</p>
                        <p className="text-slate-400 text-xs mt-1">This user is not assigned to any projects</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 flex-shrink-0">
                <button
                  onClick={closeEditModal}
                  className="h-9 px-4 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-100 font-black text-[10px] uppercase tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  disabled={updating}
                  className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 font-black text-[10px] uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, color = 'default' }) => {
  const colorClasses = {
    default: 'text-slate-900',
    orange: 'text-orange-500',
    emerald: 'text-emerald-500',
    blue: 'text-blue-500',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 group hover:border-orange-300 transition-all shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-2xl font-black ${colorClasses[color]}`}>{value}</p>
          <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest mt-1">{title}</p>
        </div>
        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-all">
          {icon}
        </div>
      </div>
    </div>
  );
};

// Permission Badge Component
const PermissionBadge = ({ label, active }) => (
  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[9px] font-black uppercase ${
    active
      ? 'bg-orange-100 text-orange-600 border border-orange-200'
      : 'bg-slate-100 text-slate-400 border border-slate-200'
  }`}>
    {active ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
    {label}
  </div>
);

export default OEMUsers;
